import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import IconPickerInline, { FIELD_ICONS } from './IconPickerInline';

const ColorPicker = ({ label, value, defaultValue, onChange, onReset }) => {
  const displayValue = value || defaultValue || '#000000';
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-foreground">{label}</label>
        {onReset && value && (
          <button
            type="button"
            onClick={onReset}
            title="Reset to default (inherits input text colour)"
            className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
          >
            <Icon name="X" size={11} />
            Reset
          </button>
        )}
      </div>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={displayValue?.startsWith('#') ? displayValue : defaultValue || '#000000'}
          onChange={(e) => onChange(e?.target?.value)}
          className="w-9 h-9 rounded-md border border-border cursor-pointer p-0.5 bg-background flex-shrink-0"
        />
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e?.target?.value)}
          placeholder={defaultValue}
          className="flex-1 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  );
};

const SliderRow = ({ label, min, max, value, defaultVal, unit, onChange }) => {
  const current = value ?? defaultVal;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-xs font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">
          {current}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={current}
        onChange={(e) => onChange(parseInt(e?.target?.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
        style={{ accentColor: 'var(--color-primary)' }}
      />
      <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

// Types where an icon inside the input makes sense
const ICON_CAPABLE_TYPES = ['input', 'email', 'phone', 'number', 'url', 'date'];

const PerFieldStyling = ({ selectedElement, onUpdateElement }) => {
  if (!selectedElement) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <Icon name="MousePointerClick" size={36} color="var(--color-muted-foreground)" />
        <p className="text-sm text-muted-foreground mt-3">
          Select a field on the canvas to customize its individual styling
        </p>
      </div>
    );
  }

  const update = (key, value) => {
    onUpdateElement?.({ ...selectedElement, fieldStyle: { ...selectedElement?.fieldStyle, [key]: value } });
  };

  const fs = selectedElement?.fieldStyle || {};

  const fontWeightOptions = [
    { value: '300', label: 'Light (300)' },
    { value: '400', label: 'Regular (400)' },
    { value: '500', label: 'Medium (500)' },
    { value: '600', label: 'Semibold (600)' },
    { value: '700', label: 'Bold (700)' },
  ];

  const labelAlignOptions = [
    { value: 'top', label: 'Top (stacked)' },
    { value: 'side', label: 'Side (inline)' },
  ];

  const textAlignOptions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  const isInputField = !['button', 'content', 'media']?.includes(selectedElement?.type);

  return (
    <div className="space-y-4">
      {/* Field identity badge */}
      <div className="flex items-center gap-2 p-2.5 bg-primary/5 border border-primary/20 rounded-lg">
        <Icon name="Layers" size={14} color="var(--color-primary)" />
        <span className="text-xs font-medium text-primary truncate">
          {selectedElement?.label || selectedElement?.name || 'Selected Field'}
        </span>
        <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded capitalize">
          {selectedElement?.type}
        </span>
      </div>
      {/* Label Layout */}
      {isInputField && (
        <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="AlignLeft" size={14} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground">Label Layout</h4>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {labelAlignOptions?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => update('labelAlign', opt?.value)}
                className={`py-2 px-3 text-xs font-medium rounded-md border transition-colors ${
                  (fs?.labelAlign || 'top') === opt?.value
                    ? 'bg-primary text-primary-foreground border-primary' :'bg-background border-border hover:border-primary/50 text-muted-foreground'
                }`}
              >
                {opt?.label}
              </button>
            ))}
          </div>
          <Select
            label="Label Text Align"
            options={textAlignOptions}
            value={fs?.labelTextAlign || 'left'}
            onChange={(v) => update('labelTextAlign', v)}
          />
          <Select
            label="Label Font Weight"
            options={fontWeightOptions}
            value={fs?.labelFontWeight || '500'}
            onChange={(v) => update('labelFontWeight', v)}
          />
        </div>
      )}
      {/* Input Dimensions */}
      {isInputField && (
        <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Maximize2" size={14} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground">Input Dimensions</h4>
          </div>
          <SliderRow
            label="Input Height"
            min={28}
            max={72}
            value={fs?.inputHeight}
            defaultVal={40}
            unit="px"
            onChange={(v) => update('inputHeight', v)}
          />
          <SliderRow
            label="Padding (horizontal)"
            min={4}
            max={32}
            value={fs?.paddingX}
            defaultVal={12}
            unit="px"
            onChange={(v) => update('paddingX', v)}
          />
          <SliderRow
            label="Padding (vertical)"
            min={4}
            max={24}
            value={fs?.paddingY}
            defaultVal={8}
            unit="px"
            onChange={(v) => update('paddingY', v)}
          />
          <SliderRow
            label="Border Radius"
            min={0}
            max={24}
            value={fs?.borderRadius}
            defaultVal={6}
            unit="px"
            onChange={(v) => update('borderRadius', v)}
          />
        </div>
      )}
      {/* Typography */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Type" size={14} color="var(--color-primary)" />
          <h4 className="text-sm font-semibold text-foreground">Typography</h4>
        </div>
        <Select
          label="Input Font Weight"
          options={fontWeightOptions}
          value={fs?.inputFontWeight || '400'}
          onChange={(v) => update('inputFontWeight', v)}
        />
        <SliderRow
          label="Font Size"
          min={10}
          max={24}
          value={fs?.fontSize}
          defaultVal={14}
          unit="px"
          onChange={(v) => update('fontSize', v)}
        />
      </div>
      {/* Colors */}
      {isInputField && (
        <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Palette" size={14} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground">Colors</h4>
          </div>
          <ColorPicker
            label="Input Background"
            value={fs?.inputBg}
            defaultValue="#ffffff"
            onChange={(v) => update('inputBg', v)}
          />
          <ColorPicker
            label="Input Border Color"
            value={fs?.borderColor}
            defaultValue="#e5e7eb"
            onChange={(v) => update('borderColor', v)}
          />
          <ColorPicker
            label="Input Text Color"
            value={fs?.textColor}
            defaultValue="#111827"
            onChange={(v) => update('textColor', v)}
          />
          <ColorPicker
            label="Label Color"
            value={fs?.labelColor}
            defaultValue="#374151"
            onChange={(v) => update('labelColor', v)}
          />
        </div>
      )}
      {/* Icon */}
      {ICON_CAPABLE_TYPES?.includes(selectedElement?.type) && (
        <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Smile" size={14} color="var(--color-primary)" />
            <h4 className="text-sm font-semibold text-foreground">Input Icon</h4>
            {fs?.iconName && (
              <button
                type="button"
                onClick={() => onUpdateElement?.({ ...selectedElement, fieldStyle: { ...fs, iconName: undefined, iconPosition: undefined } })}
                className="ml-auto text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              >
                <Icon name="X" size={11} />
                Clear
              </button>
            )}
          </div>
          <IconPickerInline value={fs?.iconName} onChange={(v) => update('iconName', v)} />
          {fs?.iconName && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">Icon Position</label>
              <div className="grid grid-cols-2 gap-2">
                {['left', 'right']?.map(pos => (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => update('iconPosition', pos)}
                    className={`py-2 px-3 text-xs font-medium rounded-md border transition-colors ${
                      (fs?.iconPosition || 'left') === pos
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/50 text-muted-foreground'
                    }`}
                  >
                    {pos === 'left' ? 'Left' : 'Right'}
                  </button>
                ))}
              </div>
            </div>
          )}
          {fs?.iconName && (
            <ColorPicker
              label="Icon Color"
              value={fs?.iconColor}
              defaultValue={fs?.textColor || '#9ca3af'}
              onChange={(v) => update('iconColor', v)}
              onReset={() => {
                const { iconColor, ...rest } = selectedElement?.fieldStyle || {};
                onUpdateElement?.({ ...selectedElement, fieldStyle: rest });
              }}
            />
          )}
        </div>
      )}
      {/* Spacing */}
      <div className="p-3.5 bg-muted/20 rounded-lg border border-border/60 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Move" size={14} color="var(--color-primary)" />
          <h4 className="text-sm font-semibold text-foreground">Field Spacing</h4>
        </div>
        <SliderRow
          label="Margin Bottom"
          min={0}
          max={48}
          value={fs?.marginBottom}
          defaultVal={16}
          unit="px"
          onChange={(v) => update('marginBottom', v)}
        />
      </div>
      {/* Reset */}
      <button
        onClick={() => onUpdateElement?.({ ...selectedElement, fieldStyle: {} })}
        className="w-full py-2 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-muted/50 hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
      >
        <Icon name="RotateCcw" size={12} />
        Reset field styles to defaults
      </button>
    </div>
  );
};

export default PerFieldStyling;
